import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { cn } from "../lib/utils";
import { format } from "date-fns";
import { CalendarIcon, ChevronDown, SlashIcon } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import LoadingButton from "./LoadingButton";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Switch } from "./ui/switch";
import { UnitSystemContext } from "../context/UnitSystemContext";
import { useContext } from "react";
import { useProjectId } from "../context/ProjectIdContext";
import { useAuth } from "../context/AuthContext";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { useProjectInfo } from "../context/ProjectInfoContext";
import { useApplicator } from "../context/ApplicatorContext";
import UpdateSIPPInstaller from "./UpdateSIPPInstaller";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL
const ProjectInfo = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const { applicatorId, companyName, setCompanyName } = useApplicator();

  const { userSession } = useAuth(); // Get session from context

  const userType = userSession?.userType;

  const location = useLocation();
  const isEditing = location.state?.isEditing || false;

  // const { setProjectData } = useProjectInfo();

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState("ProjectInfo");
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState();
  const [closeDate, setCloseDate] = useState();
  // const [workingPressure, setWorkingPressure] = useState(100);
  // const [soilDensity, setSoilDensity] = useState(120);
  // const [modulusOfSoilRtn, setModulusOfSoilRtn] = useState(1000);
  const { projectData, setProjectData } = useProjectInfo();

  const { workingPressure, soilDensity, modulusOfSoilRtn } = projectData;

  const [resin, setResin] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const { unitSystem, setUnitSystem } = useContext(UnitSystemContext);
  const [toastList, setToastList] = useState([]);

  const { projectId, setProjectId } = useProjectId();

  // useEffect(() => {
  //   const savedUnit = localStorage.getItem("savedUnitSystem");
  //   if (savedUnit) {
  //     setUnitSystem(savedUnit);
  //   } else {
  //     setUnitSystem("imperial"); // Ensure default reset
  //   }
  // }, [setUnitSystem]);

  // Load existing project details if editing
  useEffect(() => {
    const savedProject = JSON.parse(localStorage.getItem("projectInfo"));

    if (isEditing && savedProject) {
      // console.log(savedProject);
      setProjectInfo((prev) => ({
        ...prev,
        tenderId: savedProject.tenderId ?? "",
        title: savedProject.title ?? "",
        location: savedProject.location ?? "",
      }));

      setCustomerInfo((prev) => ({
        ...prev,
        clientName: savedProject.clientName ?? "",
        contactFullName: savedProject.contactFullName ?? "",
        contactEmail: savedProject.contactEmail ?? "",
        contactPhone: savedProject.contactPhone ?? "",
        city: savedProject.city ?? "",
        state: savedProject.state ?? "",
      }));

      // setProjectData((prev) => ({
      //   ...prev,
      //   workingPressure: savedProject.workingPressure ?? prev.workingPressure,
      //   soilDensity: savedProject.soilDensity ?? prev.soilDensity,
      //   modulusOfSoilRtn:
      //     savedProject.modulusOfSoilRtn ?? prev.modulusOfSoilRtn,
      //   resin: savedProject.resin ?? prev.resin,
      //   sippInstaller: savedProject.sippInstaller ?? prev.sippInstaller,
      // }));

      setStartDate(
        savedProject.startDate ? new Date(savedProject.startDate) : null,
      );
      setCloseDate(
        savedProject.closeDate ? new Date(savedProject.closeDate) : null,
      );
      setSelectedOption(savedProject.projectType || null);

      // Update `useProjectInfo` context to keep values globally accessible
      setProjectData((prev) => ({
        ...prev,
        workingPressure: savedProject.workingPressure ?? prev.workingPressure,
        soilDensity: savedProject.soilDensity ?? prev.soilDensity,
        modulusOfSoilRtn:
          savedProject.modulusOfSoilRtn ?? prev.modulusOfSoilRtn,
        resin: savedProject.resin ?? prev.resin,
        sippInstaller: savedProject.sippInstaller ?? prev.sippInstaller,
      }));
    }
  }, [isEditing, setProjectData, projectId]);

  //  Fetch company name if missing
  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    const fetchApplicatorDetails = async () => {
      if (!applicatorId || companyName) return; // If companyName exists, no need to fetch

      try {
        const response = await fetch(
          `${API_URL}/api/applicator/${applicatorId}`,
          {
            method: "GET",
            headers: { Authorization: `Basic ${encodedCredentials}` },
          },
        );

        if (response.ok) {
          const responseData = await response.json();
          // console.log(responseData)
          setCompanyName(responseData?.data?.applicator?.companyName);

          // console.log("companyName state updated:", companyName);

          setProjectData((prev) => ({
            ...prev,
            sippInstaller: responseData?.data?.applicator?.companyName,
          }));
        }
      } catch (error) {
        console.error("Error fetching applicator details:", error);
      }
    };

    fetchApplicatorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicatorId, companyName]);

  // useEffect(() => {
  //   const savedData = localStorage.getItem("projectInfo");
  //   if (savedData) {
  //     // const { workingPressure, soilDensity, modulusOfSoilRtn, resin } =
  //     //   JSON.parse(savedData);
  //     setProjectData((prev) => ({
  //       ...prev,
  //       workingPressure: savedData.workingPressure ?? prev.workingPressure,
  //       soilDensity: savedData.soilDensity ?? prev.soilDensity,
  //       modulusOfSoilRtn: savedData.modulusOfSoilRtn ?? prev.modulusOfSoilRtn,
  //       resin: savedData.resin ?? prev.resin,
  //     }));
  //   }
  // }, [setProjectData]);

  const [customerInfo, setCustomerInfo] = useState(() => {
    const savedProject = JSON.parse(localStorage.getItem("projectInfo"));
    return savedProject
      ? {
          clientName: savedProject.clientName ?? "",
          contactFullName: savedProject.contactFullName ?? "",
          contactEmail: savedProject.contactEmail ?? "",
          contactPhone: savedProject.contactPhone ?? "",
          city: savedProject.city ?? "",
          state: savedProject.state ?? "",
        }
      : {
          clientName: "",
          contactFullName: "",
          contactEmail: "",
          contactPhone: "",
          city: "",
          state: "",
        };
  });

  // const [installerInfo, setInstallerInfo] = useState({
  //   sippInstaller: "",
  //   sippCalcDesignDate: "", // Current date
  //   installerId: "",
  // });

  // Auto-fill `sippInstaller` and `sippCalcDesignDate`
  // useEffect(() => {
  //   if (companyName) {
  //     setProjectInfo((prev) => ({ ...prev, sippInstaller: companyName }));
  //   }

  //   const todayDate = new Date().toISOString().split("T")[0];
  //   setProjectInfo((prev) => ({
  //     ...prev,
  //     sippCalcDesignDate: todayDate,
  //   }));
  // }, [companyName]);

  useEffect(() => {
    const todayDate = new Date().toISOString().split("T")[0];
    // console.log("companyName state updated:", companyName);
    setProjectInfo((prev) => ({
      ...prev,
      sippInstaller: companyName || prev.sippInstaller,
      sippCalcDesignDate: todayDate,
    }));
  }, [companyName]);

  // // Handle Input Changes
  // const handleInstallerInputChange = (field) => (e) => {
  //   setProjectInfo((prev) => ({ ...prev, [field]: e.target.value }));
  // };

  const [projectInfo, setProjectInfo] = useState(() => {
    const savedProject = JSON.parse(localStorage.getItem("projectInfo"));
    return savedProject
      ? {
          tenderId: savedProject.tenderId ?? "",
          title: savedProject.title ?? "",
          location: savedProject.location ?? "",
        }
      : {
          tenderId: "",
          title: "",
          location: "",
        };
  });

  // eslint-disable-next-line no-unused-vars
  const [calcData, setCalcData] = useState({
    workingPressure: "",
    soilDensity: "",
    modulusOfSoilRtn: "",
    resin: "",
  });

  const isCustomerInfoComplete = Object.values(customerInfo).every(
    (val) => val.trim() !== "",
  );

  const isProjectInfoComplete = Object.values({
    ...(selectedOption === "Tender"
      ? {
          tenderId: projectInfo.tenderId,
          startDate,
          closeDate,
        }
      : {}),
    title: projectInfo.title,
    location: projectInfo.location,
    workingPressure,
    soilDensity,
    modulusOfSoilRtn,
    resin,
  }).every((val) => val !== undefined && val !== null && val !== "");

  // console.log("Before Submit: projectInfo =", projectInfo);

  // const isInstallerInfoComplete = Object.values(installerInfo).every(
  //   (val) => val.trim() !== ""
  // );

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem("projectInfo"));
    if (savedData) {
      // const { workingPressure, soilDensity, modulusOfSoilRtn, resin } =
      //   JSON.parse(savedData);
      setCustomerInfo(savedData.customerInfo ?? {});
      // setInstallerInfo(savedData.installerInfo ?? {});
      setProjectInfo({
        tenderId: savedData.tenderId ?? "",
        title: savedData.title ?? "",
        location: savedData.location ?? "",
      });

      setProjectData((prev) => ({
        ...prev,
        workingPressure: savedData.workingPressure ?? prev.workingPressure,
        soilDensity: savedData.soilDensity ?? prev.soilDensity,
        modulusOfSoilRtn: savedData.modulusOfSoilRtn ?? prev.modulusOfSoilRtn,
        resin: savedData.resin ?? prev.resin,
        sippInstaller: savedData.sippInstaller ?? prev.sippInstaller,
      }));

      setStartDate(savedData.startDate ? new Date(savedData.startDate) : null);
      setCloseDate(savedData.closeDate ? new Date(savedData.closeDate) : null);
      setSelectedOption(savedData.projectType || null);
    }
  }, [setProjectData]);

  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 15);

    setCustomerInfo((prev) => ({
      ...prev,
      contactPhone: digitsOnly,
    }));
  };

  const handleInputChange = (setter, field) => (e) => {
    setter((prev) => {
      const updated = { ...prev, [field]: e.target.value };
      return updated;
    });

    // Call storage update **after** setter to ensure value is included
    setTimeout(() => saveToLocalStorage(), 0);
  };

  const saveToLocalStorage = () => {
    localStorage.setItem(
      "projectInfo",
      JSON.stringify({
        customerInfo,
        // installerInfo,
        sippInstaller: companyName,
        projectInfo,
        workingPressure: workingPressure,
        soilDensity: soilDensity,
        modulusOfSoilRtn: modulusOfSoilRtn,
        resin,
        startDate,
        closeDate,
        selectedOption,
      }),
    );
  };

  // console.log(projectInfo);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidPhone = (phone) => /^\d{7,15}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(customerInfo.contactEmail)) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Please enter a valid email address.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
      return;
    }

    if (!isValidPhone(customerInfo.contactPhone)) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Phone number must contain 7–15 digits only.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
      return;
    }

    if (userType === "customer") {
      // For customer, skip POST and navigate directly
      setProjectData((prev) => ({
        ...prev,
        workingPressure,
        soilDensity,
        modulusOfSoilRtn,
        resin,
      }));

      const saveToLocalStorage = () => {
        localStorage.setItem(
          "projectInfo",
          JSON.stringify({
            workingPressure: workingPressure,
            soilDensity: soilDensity,
            modulusOfSoilRtn: modulusOfSoilRtn,
            resin,
            sippInstaller: companyName,
            selectedOption,
          }),
        );
      };

      saveToLocalStorage();

      // Save data to localStorage
      const savedData = {
        selectedOption,
        workingPressure,
        soilDensity,
        modulusOfSoilRtn,
        resin,
      };
      localStorage.setItem("projectInfo", JSON.stringify(savedData));

      navigate("/calculator");

      setCurrentPage("Calculator");

      return;
    }

    setIsLoading(true);

    if (!isEditing && (!isCustomerInfoComplete || !isProjectInfoComplete)) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Please fill out all required fields.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
      return;
    }

    if (!companyName) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description:
            "No company name found. Please register an applicator first.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
      return;
    }

    let requestBody = {
      title: projectInfo.title,
      location: projectInfo.location,
      clientName: customerInfo.clientName,
      sippInstaller: companyName,
      sippCalcDesignDate: new Date().toISOString().split("T")[0], // Current date
      contactFullName: customerInfo.contactFullName,
      contactEmail: customerInfo.contactEmail,
      contactPhone: customerInfo.contactPhone,
      city: customerInfo.city,
      state: customerInfo.state,
      projectType: selectedOption || "", // Include the selected project type
    };

    //  Only add `tenderId`, `startDate`, and `closeDate` if "Tender" is selected
    if (selectedOption === "Tender") {
      requestBody.tenderId = projectInfo.tenderId;
      requestBody.startDate = startDate ? format(startDate, "yyyy-MM-dd") : "";
      requestBody.closeDate = closeDate ? format(closeDate, "yyyy-MM-dd") : "";
    }

    console.log("Payload:", requestBody);

    if (!isEditing) {
      // Create new project
      try {
        const token = localStorage.getItem("x-auth-token");
        // console.log(token);
        const response = await fetch(`${API_URL}/api/project`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
            "x-auth-token": token,
            "unit-type": unitSystem === "imperial" ? "imperial" : "si",
          },
          body: JSON.stringify(requestBody),
        });

        const result = await response.json();

        const saveToLocalStorage = () => {
          localStorage.setItem(
            "projectInfo",
            JSON.stringify({
              customerInfo,
              sippInstaller: companyName,
              // installerInfo,
              projectInfo,
              workingPressure: workingPressure,
              soilDensity: soilDensity,
              modulusOfSoilRtn: modulusOfSoilRtn,
              resin,
              startDate,
              closeDate,
              selectedOption,
            }),
          );
        };

        // console.log(result);

        if (result.data.success) {
          const projectId = result.data.savedProject._id;
          setProjectId(projectId); // Save to context

          setCurrentPage("Calculator");

          saveToLocalStorage();

          setToastList((prev) => [
            ...prev,
            {
              id: Date.now(),
              title: "SUCCESS!",
              description: result.data?.message || result?.error || "!",
              variant: "default",
            },
          ]);

          // Save data to localStorage
          const savedData = {
            customerInfo,
            sippInstaller: companyName,
            // installerInfo,
            projectInfo,
            startDate,
            closeDate,
            selectedOption,
            workingPressure,
            soilDensity,
            modulusOfSoilRtn,
            resin,
          };
          localStorage.setItem("projectInfo", JSON.stringify(savedData));

          // Store data in context
          setProjectData((prev) => ({
            ...prev,
            workingPressure,
            soilDensity,
            modulusOfSoilRtn,
            resin,
            sippInstaller: companyName,
          }));

          navigate("/projectlist");
        } else {
          setToastList((prev) => [
            ...prev,
            {
              id: Date.now(),
              title: "ERROR!",
              description: result.data?.message || result?.error || "!",
              variant: "destructive",
            },
          ]);
          console.error("Project creation failed:", result.data.message);
        }
      } catch (error) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description: "An error occurred.",
            variant: "destructive",
          },
        ]);
        console.error("Error during project creation:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // UPDATE existing project

      // Retrieve the original project data from localStorage
      const savedProject =
        JSON.parse(localStorage.getItem("projectInfo")) || {};

      // Create an empty object to store only changed fields
      const updatedProject = {};

      // Compare and add only changed fields
      if (projectInfo.title && projectInfo.title !== savedProject.title) {
        updatedProject.title = projectInfo.title;
      }
      if (
        projectInfo.location &&
        projectInfo.location !== savedProject.location
      ) {
        updatedProject.location = projectInfo.location;
      }
      if (
        customerInfo.clientName &&
        customerInfo.clientName !== savedProject.clientName
      ) {
        updatedProject.clientName = customerInfo.clientName;
      }
      if (
        customerInfo.contactFullName &&
        customerInfo.contactFullName !== savedProject.contactFullName
      ) {
        updatedProject.contactFullName = customerInfo.contactFullName;
      }
      if (
        customerInfo.contactEmail &&
        customerInfo.contactEmail !== savedProject.contactEmail
      ) {
        updatedProject.contactEmail = customerInfo.contactEmail;
      }
      if (
        customerInfo.contactPhone &&
        customerInfo.contactPhone !== savedProject.contactPhone
      ) {
        updatedProject.contactPhone = customerInfo.contactPhone;
      }
      if (customerInfo.city && customerInfo.city !== savedProject.city) {
        updatedProject.city = customerInfo.city;
      }
      if (customerInfo.state && customerInfo.state !== savedProject.state) {
        updatedProject.state = customerInfo.state;
      }
      if (selectedOption && selectedOption !== savedProject.projectType) {
        updatedProject.projectType = selectedOption;
      }
      if (
        projectInfo.tenderId &&
        projectInfo.tenderId !== savedProject.tenderId
      ) {
        updatedProject.tenderId = projectInfo.tenderId;
      }
      if (startDate && startDate !== new Date(savedProject.startDate)) {
        updatedProject.startDate = format(startDate, "yyyy-MM-dd");
      }
      if (closeDate && closeDate !== new Date(savedProject.closeDate)) {
        updatedProject.closeDate = format(closeDate, "yyyy-MM-dd");
      }

      // Add new fields to be updated if they have changed
      if (resin !== savedProject.resin) {
        updatedProject.resin = resin;
      }
      if (workingPressure && workingPressure !== savedProject.workingPressure) {
        updatedProject.workingPressure = workingPressure;
      }
      if (soilDensity && soilDensity !== savedProject.soilDensity) {
        updatedProject.soilDensity = soilDensity;
      }
      if (
        modulusOfSoilRtn &&
        modulusOfSoilRtn !== savedProject.modulusOfSoilRtn
      ) {
        updatedProject.modulusOfSoilRtn = modulusOfSoilRtn;
      }

      // If no fields have changed, prevent unnecessary API call
      if (Object.keys(updatedProject).length === 0) {
        console.log("No changes detected, skipping update.");
        return;
      }

      const token = localStorage.getItem("x-auth-token");
      const response = await fetch(`${API_URL}/api/project/${projectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
        body: JSON.stringify(updatedProject),
      });

      const result = await response.json();
      if (response.ok) {
        // Merge updates into localStorage
        const updatedData = { ...savedProject, ...updatedProject };

        setProjectData((prev) => ({
          ...prev,
          resin: updatedData.resin ?? prev.resin,
        }));

        // Update useProjectInfo context with new values if they changed
        setProjectData((prev) => ({
          ...prev,
          resin: updatedData.resin ?? prev.resin,
          workingPressure:
            updatedProject.workingPressure ?? prev.workingPressure,
          soilDensity: updatedProject.soilDensity ?? prev.soilDensity,
          modulusOfSoilRtn:
            updatedProject.modulusOfSoilRtn ?? prev.modulusOfSoilRtn,
        }));

        // Update localStorage with new project info
        localStorage.setItem("projectInfo", JSON.stringify(updatedData));

        // Show success message
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: "Project updated successfully",
            variant: "default",
          },
        ]);

        navigate("/projectlist"); // Redirect after update
      } else {
        throw new Error(result.message || "Failed to update project");
      }
    }
  };

  const breadcrumbs = [
    {
      label: "Project Information",
      link: "/projectinfo",
      isCurrent: currentPage === "ProjectInfo",
    },
    {
      label: "Calculator",
      link: "/calculator",
      isCurrent: currentPage === "Calculator",
    },
    {
      label: "Design Summary",
      link: "/designsummary",
      isCurrent: currentPage === "Design Summary",
    },
  ];

  const options = ["Tender", "Sole Source", "Estimate", "Others"];

  const handleOptionClick = (e, option) => {
    e.preventDefault();
    setSelectedOption(option);

    // Save to localStorage immediately
    const savedProject = JSON.parse(localStorage.getItem("projectInfo")) || {};
    localStorage.setItem(
      "projectInfo",
      JSON.stringify({ ...savedProject, selectedOption: option }),
    );
  };

  // const handleInitialConversionToSI =  useCallback( async () => {
  //   try {
  //     const token = localStorage.getItem("x-auth-token");

  //     const response = await fetch(
  //       `${API_URL}/api/calculate/convert-request-data`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Basic ${encodedCredentials}`,
  //           "x-auth-token": token,
  //           "convertion-type": "IMPERIAL_TO_SI",
  //         },
  //         body: JSON.stringify({
  //           soilDensity: Number(soilDensity) || 0,
  //           modulusOfSoilRtn: Number(modulusOfSoilRtn) || 0,
  //           workingPressure: Number(workingPressure) || 0,
  //         }),
  //       },
  //     );

  //     if (!response.ok) throw new Error("Initial conversion failed");

  //     const result = await response.json();
  //     const converted = result.data.requestData;

  //     setSoilDensity(converted.soilDensity ?? "");
  //     setModulusOfSoilRtn(converted.modulusOfSoilRtn ?? "");
  //     setWorkingPressure(converted.workingPressure ?? "");
  //   } catch (error) {
  //     console.error("Initial unit sync failed:", error);
  //   }
  // }, [encodedCredentials, modulusOfSoilRtn, soilDensity, workingPressure])

  // const isDataReady =
  // soilDensity !== "" &&
  // modulusOfSoilRtn !== "" &&
  // workingPressure !== "";

  // useEffect(() => {
  //   const savedUnit = localStorage.getItem("savedUnitSystem");

  //   if (!savedUnit) return;

  //   setUnitSystem(savedUnit);

  //   // // If saved unit is SI, convert defaults immediately
  //   // if (savedUnit === "si") {
  //   //   handleInitialConversionToSI();
  //   // }

  //   // Only convert if SI is saved AND data exists
  // if (savedUnit === "si" && isDataReady) {
  //   handleInitialConversionToSI();
  // }

  // }, [setUnitSystem, handleInitialConversionToSI, isDataReady]);

  // const updateUnits = (system) => {
  //   setCalcData((prevCalcData) => ({
  //     ...prevCalcData,
  //   }));
  // };

  // const handleSliderChange = async (checked) => {
  //   const selectedUnitSystem = checked ? "si" : "imperial";
  //   setUnitSystem(selectedUnitSystem);
  //   updateUnits(selectedUnitSystem);

  //   const conversionType = checked ? "IMPERIAL_TO_SI" : "SI_TO_IMPERIAL";

  //   const requestBody = {
  //     soilDensity: soilDensity,
  //     modulusOfSoilRtn: modulusOfSoilRtn,
  //     workingPressure: workingPressure,
  //   };

  //   try {
  //     const token = localStorage.getItem("x-auth-token");
  //     const response = await fetch(
  //       `${API_URL}/api/calculate/convert-request-data`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Basic ${encodedCredentials}`,
  //           "x-auth-token": token,
  //           "convertion-type": conversionType,
  //         },
  //         body: JSON.stringify(requestBody),
  //       },
  //     );

  //     const result = await response.json();
  //     if (response.ok) {
  //       const convertedData = result.data.requestData;

  //       setSoilDensity(convertedData.soilDensity || 0);
  //       setModulusOfSoilRtn(convertedData.modulusOfSoilRtn || 0);
  //       setWorkingPressure(convertedData.workingPressure || 0);

  //       // setToastList((prev) => [
  //       //   ...prev,
  //       //   {
  //       //     id: Date.now(),
  //       //     title: "SUCCESS!",
  //       //     description: result.message || "Units converted successfully",
  //       //     variant: "default",
  //       //   },
  //       // ]);
  //     } else {
  //       console.error("Conversion API Error:", result);
  //       throw new Error(result.message || "Conversion failed");
  //     }
  //   } catch (error) {
  //     console.error("Error converting units:", error);
  //     setToastList((prev) => [
  //       ...prev,
  //       {
  //         id: Date.now(),
  //         title: "ERROR!",
  //         description: "Failed to convert units",
  //         variant: "destructive",
  //       },
  //     ]);
  //   }
  // };

  const handleSliderChange = async (checked) => {
    const newUnit = checked ? "si" : "imperial";

    //  STOP if already in this unit
    if (unitSystem === newUnit) return;

    const conversionType = checked ? "IMPERIAL_TO_SI" : "SI_TO_IMPERIAL";
    // const previousUnit = unitSystem;

    setUnitSystem(newUnit);
    localStorage.setItem("savedUnitSystem", newUnit);

    // const conversionType =
    //   previousUnit === "imperial" ? "IMPERIAL_TO_SI" : "SI_TO_IMPERIAL";

    const requestBody = {
      soilDensity: Number(soilDensity),
      modulusOfSoilRtn: Number(modulusOfSoilRtn),
      workingPressure: Number(workingPressure),
    };

    try {
      const token = localStorage.getItem("x-auth-token");
      // console.log(token);
      const response = await fetch(
        `${API_URL}/api/calculate/convert-request-data`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`,
            "x-auth-token": token,
            "convertion-type": conversionType,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) throw new Error("Conversion failed");

      const result = await response.json();
      const converted = result.data.requestData;

      // console.log("----- UNIT TOGGLE -----");
      // console.log("Previous unit:", unitSystem);
      // console.log("New unit:", newUnit);
      // console.log("Conversion type:", conversionType);

      // console.log("Converted values:", converted);

      const round = (num, decimals = 2) =>
        Number(Number(num).toFixed(decimals));

      setProjectData((prev) => ({
        ...prev,
        soilDensity: round(converted.soilDensity),
        modulusOfSoilRtn: round(converted.modulusOfSoilRtn),
        workingPressure: round(converted.workingPressure),
      }));
    } catch (error) {
      console.error("Unit conversion error:", error);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <main className="tw-flex tw-flex-col -tw-mt-44">
      {/* Breadcrumbs */}
      <div className="tw-absolute tw-top-44 tw-left-36 tw-flex tw-justify-between tw-w-[71%]">
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem>
                  {breadcrumb.isCurrent ? (
                    // Render the current page without a link
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  ) : (
                    // Render the previous pages without links
                    <BreadcrumbLink>{breadcrumb.label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && (
                  <BreadcrumbSeparator>
                    <SlashIcon />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="tw-flex tw-gap-0">
          <UpdateSIPPInstaller />

          {userType === "applicator" && (
            <div className="">
              <Link to="/projectlist">
                <Button className="tw-hidden md:tw-flex tw-bg-[#00B5ED] tw-text-white hover:tw-bg-[#009ECC]">
                  All Projects
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="tw-flex tw-flex-col  md:tw-flex-row  md:tw-justify-center md:tw-items-center">
        {/* Left side (white background visible on both desktop and mobile) */}
        <div className="tw-flex-1 tw-flex tw-items-center md:tw-justify-center tw-mt-24 md:tw-mt-10 h-sm:tw-mt-10">
          <div className="md:tw-w-[60%] tw-w-[100%] md:tw-mt-60 tw-mt-2">
            <form onSubmit={handleSubmit}>
              {userType === "applicator" && (
                <Card className="tw-w-full tw-shadow-none tw-border-none tw-mb-0 tw-bg-transparent">
                  <CardHeader className="tw-px-0">
                    <CardTitle className="tw-text-lg md:tw-text-2xl h-sm:tw-text-xl">
                      Customer Information
                    </CardTitle>
                  </CardHeader>

                  <Collapsible defaultOpen={true}>
                    <CollapsibleTrigger asChild>
                      <div className="tw-flex tw-items-center tw-justify-between md:tw-mt-1">
                        <h3 className="tw-text-xs md:tw-text-sm">
                          Fill out customer information
                        </h3>
                        <ChevronDown className="tw-h-3" />
                      </div>
                    </CollapsibleTrigger>

                    {/* Grid layout with 2 columns */}
                    <CollapsibleContent asChild>
                      <CardContent className="tw-px-0">
                        <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8 tw-mt-3">
                          <div className="tw-grid tw-gap-2">
                            <Label
                              className="tw-font-medium"
                              htmlFor="clientName"
                            >
                              Client Name
                            </Label>
                            <Input
                              id="clientName"
                              type="text"
                              value={customerInfo.clientName}
                              onChange={handleInputChange(
                                setCustomerInfo,
                                "clientName",
                              )}
                              onKeyDown={(e) => {
                                if (
                                  e.key === "ArrowUp" ||
                                  e.key === "ArrowDown"
                                ) {
                                  e.preventDefault(); // Prevent increment/decrement
                                }
                              }}
                              required
                              className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                            />
                          </div>
                          {[
                            {
                              label: "Contact Person Full Name",
                              id: "contactFullName",
                              type: "text",
                            },
                          ].map(({ label, id, type }) => (
                            <div key={id} className="tw-grid tw-gap-2">
                              <Label className="tw-font-medium" htmlFor={id}>
                                {label}
                              </Label>
                              <Input
                                id={id}
                                type={type}
                                value={customerInfo[id]}
                                onWheel={(e) => e.target.blur()}
                                onChange={handleInputChange(
                                  setCustomerInfo,
                                  id,
                                )}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "ArrowUp" ||
                                    e.key === "ArrowDown"
                                  ) {
                                    e.preventDefault(); // Prevent increment/decrement
                                  }
                                }}
                                required
                                className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                              />
                            </div>
                          ))}

                          <div className="tw-grid tw-gap-2">
                            <Label className="tw-font-medium" htmlFor="title">
                              Contact Person Email
                            </Label>
                            <Input
                              id="contactEmail"
                              type="email"
                              value={customerInfo.contactEmail}
                              onChange={handleInputChange(
                                setCustomerInfo,
                                "contactEmail",
                              )}
                              required
                              pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                              title="Please enter a valid email address (e.g. name@example.com)"
                              className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                            />
                          </div>

                          <div className="tw-grid tw-gap-2">
                            <Label className="tw-font-medium" htmlFor="title">
                              Contact Person Phone Number
                            </Label>
                            <Input
                              id="contactPhone"
                              type="tel"
                              value={customerInfo.contactPhone}
                              onChange={handlePhoneChange}
                              inputMode="numeric"
                              pattern="^\d{7,15}$"
                              title="Phone number must contain 7–15 digits only"
                              required
                              className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                            />
                          </div>

                          <Collapsible defaultOpen={true}>
                            <CollapsibleTrigger asChild>
                              <div className="tw-flex tw-items-center tw-justify-start md:tw-mb-2">
                                <h3 className="tw-text-xs md:tw-text-sm">
                                  Location
                                </h3>
                                <ChevronDown className="tw-h-3" />
                              </div>
                            </CollapsibleTrigger>

                            <CollapsibleContent asChild>
                              <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8 tw-mt-3">
                                {[
                                  { label: "City", id: "city", type: "text" },
                                  {
                                    label: "State",
                                    id: "state",
                                    type: "text",
                                  },
                                ].map(({ label, id, type }) => (
                                  <div key={id} className="tw-grid tw-gap-2">
                                    <Label
                                      className="tw-font-medium"
                                      htmlFor={id}
                                    >
                                      {label}
                                    </Label>
                                    <Input
                                      id={id}
                                      type={type}
                                      value={customerInfo[id]}
                                      onChange={handleInputChange(
                                        setCustomerInfo,
                                        id,
                                      )}
                                      required
                                      className="tw-w-full placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                                    />
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              )}

              <Card className="tw-w-full tw-shadow-none tw-border-none tw-mb-0 tw-bg-transparent tw-mt-6">
                <CardHeader className="tw-px-0">
                  <CardTitle className="tw-text-lg md:tw-text-2xl h-sm:tw-text-xl">
                    Project Information
                  </CardTitle>
                </CardHeader>

                <Collapsible defaultOpen={true}>
                  <CollapsibleTrigger asChild>
                    <div className="tw-flex tw-items-center tw-justify-between md:tw-mt-1">
                      <h3 className="tw-text-xs md:tw-text-sm">
                        Fill out project information
                      </h3>
                      <ChevronDown className="tw-h-3" />
                    </div>
                  </CollapsibleTrigger>

                  {/* Grid layout with 2 columns */}
                  <CollapsibleContent asChild>
                    <CardContent className="tw-px-0 ">
                      <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8 tw-mt-3">
                        {userType === "applicator" && (
                          <>
                            <div className="tw-grid tw-gap-2">
                              <Label className="tw-font-medium" htmlFor="title">
                                Project Title
                              </Label>
                              <Input
                                id="title"
                                type="text"
                                value={projectInfo.title}
                                onChange={handleInputChange(
                                  setProjectInfo,
                                  "title",
                                )}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "ArrowUp" ||
                                    e.key === "ArrowDown"
                                  ) {
                                    e.preventDefault(); // Prevent increment/decrement
                                  }
                                }}
                                required
                                className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                              />

                              <Label className="tw-font-medium" htmlFor="title">
                                Project Location
                              </Label>
                              <Input
                                id="location"
                                type="text"
                                value={projectInfo.location}
                                onChange={handleInputChange(
                                  setProjectInfo,
                                  "location",
                                )}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "ArrowUp" ||
                                    e.key === "ArrowDown"
                                  ) {
                                    e.preventDefault(); // Prevent increment/decrement
                                  }
                                }}
                                required
                                className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                              />
                            </div>

                            {/* <div className="tw-grid tw-gap-2">
                              <Label
                                className="tw-font-medium"
                                htmlFor="sippInstaller"
                              >
                                SIPP Installer
                              </Label>
                              <div className="tw-flex">
                                <Input
                                  id="sippInstaller"
                                  value={projectInfo.sippInstaller}
                                  type="text"
                                  onChange={handleInstallerInputChange(
                                    "sippInstaller"
                                  )}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "ArrowUp" ||
                                      e.key === "ArrowDown"
                                    ) {
                                      e.preventDefault(); // Prevent increment/decrement
                                    }
                                  }}
                                  // readOnly
                                  required
                                  className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                                />
                              </div>
                            </div>

                            <div className="tw-grid tw-gap-2">
                              <Label
                                className="tw-font-medium"
                                htmlFor="sippCalcDesignDate"
                              >
                                SIPP Calc Design Date
                              </Label>
                              <div className="tw-flex">
                                <Input
                                  id="sippCalcDesignDate"
                                  value={projectInfo.sippCalcDesignDate}
                                  type="text"
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "ArrowUp" ||
                                      e.key === "ArrowDown"
                                    ) {
                                      e.preventDefault(); // Prevent increment/decrement
                                    }
                                  }}
                                  readOnly
                                  required
                                  className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                                />
                              </div>
                            </div> */}
                          </>
                        )}

                        {/* Options to select from */}
                        {userType === "applicator" && (
                          <div className="tw-flex tw-space-x-2 tw-mt-4 tw-justify-between md:tw-justify-start">
                            {options.map((option) => (
                              <button
                                key={option}
                                onClick={(e) => handleOptionClick(e, option)}
                                className={cn(
                                  "md:tw-px-4 md:tw-py-2 tw-px-1 tw-py-2 tw-rounded-md tw-font-medium tw-text-xs md:tw-text-sm tw-transition",
                                  selectedOption === option
                                    ? "tw-bg-[#00B5ED] tw-text-white hover:tw-bg-[#00a3d5] tw-font-semibold"
                                    : "tw-bg-[#F2F2F2] tw-text-black hover:tw-bg-[#e2e2e2]",
                                )}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Conditional rendering for 'Tender' children */}
                        {userType === "applicator" && (
                          <>
                            {selectedOption === "Tender" && (
                              <div className="tw-mt-0">
                                <div className="tw-grid tw-gap-4">
                                  <div>
                                    <Label
                                      htmlFor="tenderId"
                                      className="tw-font-medium"
                                    >
                                      Tender ID
                                    </Label>
                                    <Input
                                      id="tenderId"
                                      type="number"
                                      value={projectInfo.tenderId}
                                      onWheel={(e) => e.target.blur()}
                                      onChange={handleInputChange(
                                        setProjectInfo,
                                        "tenderId",
                                      )}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "ArrowUp" ||
                                          e.key === "ArrowDown"
                                        ) {
                                          e.preventDefault(); // Prevent increment/decrement
                                        }
                                      }}
                                      required
                                      className="tw-w-full no-spin placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                                    />
                                  </div>

                                  <div>
                                    <Label
                                      htmlFor="startDate"
                                      className="tw-font-medium"
                                    >
                                      Start Date
                                    </Label>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant={"outline"}
                                          className={cn(
                                            "tw-w-full data-[state=closed]:tw-bg-[#F2F2F2] tw-text-sm tw-justify-start tw-text-left tw-font-normal",
                                            !startDate &&
                                              "tw-text-muted-foreground",
                                          )}
                                        >
                                          <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4" />
                                          {startDate ? (
                                            format(startDate, "PPP")
                                          ) : (
                                            <span>Pick a start date</span>
                                          )}
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="tw-w-auto tw-p-0">
                                        <Calendar
                                          mode="single"
                                          selected={startDate}
                                          onSelect={setStartDate}
                                          initialFocus
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </div>

                                  <div>
                                    <Label
                                      htmlFor="closeDate"
                                      className="tw-font-medium"
                                    >
                                      Closing Date
                                    </Label>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant={"outline"}
                                          className={cn(
                                            "tw-w-full data-[state=closed]:tw-bg-[#F2F2F2] tw-text-sm  tw-justify-start tw-text-left tw-font-normal",
                                            !closeDate &&
                                              "tw-text-muted-foreground",
                                          )}
                                        >
                                          <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4" />
                                          {closeDate ? (
                                            format(closeDate, "PPP")
                                          ) : (
                                            <span>Pick a closing Date</span>
                                          )}
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="tw-w-auto tw-p-0">
                                        <Calendar
                                          mode="single"
                                          selected={closeDate}
                                          onSelect={setCloseDate}
                                          initialFocus
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        <div className="tw-flex tw-items-center tw-gap-2 tw-my-1">
                          <label className="tw-text-sm tw-font-medium">
                            Imperial
                          </label>
                          <Switch
                            checked={unitSystem === "imperial" ? false : true}
                            onCheckedChange={handleSliderChange}
                            className="tw-slider"
                          />
                          <span className="tw-text-sm tw-font-medium">SI</span>
                        </div>

                        <div className="tw-grid tw-gap-2">
                          <Label
                            className="tw-font-medium"
                            htmlFor="workingPressure"
                          >
                            System Working Pressure, pw
                          </Label>
                          <div className="tw-flex">
                            <Input
                              id="workingPressure"
                              value={workingPressure}
                              type="number"
                              onWheel={(e) => e.target.blur()}
                              onChange={(e) =>
                                setProjectData((prev) => ({
                                  ...prev,
                                  workingPressure: Number(e.target.value),
                                }))
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === "ArrowUp" ||
                                  e.key === "ArrowDown"
                                ) {
                                  e.preventDefault(); // Prevent increment/decrement
                                }
                              }}
                              required
                              className="tw-w-full no-spin tw-mr-2 placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                            />
                            <span className="unitsbg">
                              {unitSystem === "imperial" ? "psi" : "MPa"}
                            </span>
                          </div>
                        </div>

                        <div className="tw-grid tw-gap-2">
                          <Label
                            className="tw-font-medium"
                            htmlFor="workingPressure"
                          >
                            Soil density, w
                          </Label>
                          <div className="tw-flex">
                            <Input
                              id="soilDensity"
                              value={soilDensity}
                              type="number"
                              onWheel={(e) => e.target.blur()}
                              onChange={(e) =>
                                setProjectData((prev) => ({
                                  ...prev,
                                  soilDensity: Number(e.target.value),
                                }))
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === "ArrowUp" ||
                                  e.key === "ArrowDown"
                                ) {
                                  e.preventDefault(); // Prevent increment/decrement
                                }
                              }}
                              required
                              className="tw-w-full no-spin tw-mr-2 placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                            />
                            <span className="unitsbg">
                              {unitSystem === "imperial" ? "pcf" : "kN/m³"}
                            </span>
                          </div>
                        </div>

                        <div className="tw-grid tw-gap-2">
                          <Label
                            className="tw-font-medium"
                            htmlFor="modulusOfSoilRtn"
                          >
                            Modulus of soil rtn, E's
                          </Label>
                          <div className="tw-flex">
                            <Input
                              id="modulusOfSoilRtn"
                              value={modulusOfSoilRtn}
                              type="number"
                              onWheel={(e) => e.target.blur()}
                              onChange={(e) =>
                                setProjectData((prev) => ({
                                  ...prev,
                                  modulusOfSoilRtn: Number(e.target.value),
                                }))
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === "ArrowUp" ||
                                  e.key === "ArrowDown"
                                ) {
                                  e.preventDefault(); // Prevent increment/decrement
                                }
                              }}
                              required
                              className="tw-w-full no-spin tw-mr-2 placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs md:placeholder:tw-text-sm"
                            />
                            <span className="unitsbg">
                              {unitSystem === "imperial" ? "psi" : "MPa"}
                            </span>
                          </div>
                        </div>

                        <div className="tw-flex tw-flex-col tw-space-y-1 tw-mb-3">
                          <label htmlFor="resin" className="">
                            Resins
                          </label>

                          <div className="tw-relative tw-w-full col-sm-2 col-form-label col-form-label-sm">
                            <select
                              className="input-group select-style col-sm-2 col-form-label col-form-label-sm tw-appearance-none tw-pr-8"
                              id="resin"
                              name="resin"
                              value={resin}
                              onChange={(e) => {
                                const newResin = e.target.value;
                                setResin(newResin);

                                // Save to localStorage immediately
                                const savedProject =
                                  JSON.parse(
                                    localStorage.getItem("projectInfo"),
                                  ) || {};
                                savedProject.resin = newResin;
                                localStorage.setItem(
                                  "projectInfo",
                                  JSON.stringify(savedProject),
                                );

                                setProjectData((prev) => ({
                                  ...prev,
                                  resin: newResin,
                                }));
                              }}
                              required
                            >
                              <option hidden value="">
                                Select Resins
                              </option>
                              <option value="R320">Portable Water R320</option>
                              <option value="R330">Waste Water R330</option>
                              <option disabled value="R325">
                                Industrial R325 (Coming soon)
                              </option>
                            </select>

                            {/* Custom ChevronDown icon */}
                            <ChevronDown className="tw-absolute tw-top-1/2 tw-right-3 tw-transform -tw-translate-y-1/2 tw-pointer-events-none tw-text-gray-500 tw-h-4 tw-w-4 tw-opacity-50 md:tw-mr-2" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>

              {isLoading ? (
                <LoadingButton />
              ) : (
                <Button
                  type="submit"
                  className="tw-w-full tw-font-semibold tw-my-6 tw-text-sm tw-uppercase"
                >
                  {userType === "customer"
                    ? "Proceed to Calculator"
                    : isEditing
                      ? "Update Project"
                      : "Create Project"}
                </Button>
              )}
            </form>
          </div>
        </div>

        {/* Right side (visible as blue only on desktop) */}
        {/* <div className="tw-absolute tw-inset-x-[1100px] tw-z-0 md:tw-w-[40%] tw-hidden tw-min-h-full md:tw-flex md:tw-flex-1 tw-bg-[#f1fcff]"></div>
        <div className="tw-hidden md:tw-flex md:tw-w-[30%] tw-relative tw-min-h-screen">
          <div className="tw-flex md:tw-w-[100%] tw-justify-center tw-items-center tw-h-full tw-absolute tw-inset-x60 tw-z-0">
            <img
              src="/projectinfo.png"
              alt="Project Info Illustration"
              className="h-sm:tw-h-auto md:tw-h-auto tw-object-contain"
            />
          </div>
        </div> */}
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </main>
  );
};

export default ProjectInfo;
